import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarClock,
  FileVideo,
  Layers,
  Megaphone,
  ListOrdered,
  Radio,
  ScrollText,
  Activity,
  BarChart3,
  Settings,
  Users,
  LogOut,
  Moon,
  Sun,
  Monitor,
  ChevronDown,
  Tags,
  CreditCard,
  TrendingUp,
  Server,
  Grid3x3,
  Building2,
  Clapperboard,
  Vote,
  ScrollText as ScrollTextIcon,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { ROUTE_ROLES } from "@/lib/roleAccess";
import { wsUrl } from "@/lib/api";
import UpgradePlanDialog from "@/components/UpgradePlanDialog";
import TrialPaywall from "@/components/TrialPaywall";
import CloudflareQuotaBanner from "@/components/CloudflareQuotaBanner";

// NAV visibility uses the shared ROUTE_ROLES map from lib/roleAccess so the
// sidebar can never drift from the route-level RoleGuard. If a route is
// missing from the map it's visible to every authenticated role.
const NAV = [
  { to: "/console/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/console/scheduler", label: "Weekly Scheduler", icon: CalendarClock, testId: "nav-scheduler" },
  { to: "/console/assets", label: "Asset Library", icon: FileVideo, testId: "nav-assets" },
  { to: "/console/clips", label: "Clips", icon: Clapperboard, testId: "nav-clips" },
  { to: "/console/programs", label: "Programs & Categories", icon: Grid3x3, testId: "nav-programs" },
  { to: "/console/graphics", label: "14-Layer Graphics", icon: Layers, testId: "nav-graphics" },
  { to: "/console/magic-wall", label: "Magic Wall", icon: Vote, testId: "nav-magic-wall" },
  { to: "/console/ad-templates", label: "Ad Templates", icon: Megaphone, testId: "nav-ad-templates" },
  { to: "/console/epg", label: "EPG", icon: ListOrdered, testId: "nav-epg" },
  { to: "/console/polls", label: "Interactive Polls", icon: Radio, testId: "nav-polls" },
  { to: "/console/as-run", label: "As-Run Logs", icon: ScrollText, testId: "nav-as-run" },
  { to: "/console/monitoring", label: "Monitoring", icon: Activity, testId: "nav-monitoring" },
  { to: "/console/pricing", label: "Pricing", icon: Tags, testId: "nav-pricing" },
  { to: "/console/billing", label: "Billing", icon: CreditCard, testId: "nav-billing" },
  { to: "/console/hardware", label: "Hardware", icon: Server, testId: "nav-hardware" },
];

const ADMIN_NAV = [
  { to: "/console/tenant", label: "Tenant / Brand", icon: Settings, testId: "nav-tenant" },
  { to: "/console/users", label: "Users", icon: Users, testId: "nav-users" },
  { to: "/console/admin/revenue", label: "Revenue", icon: TrendingUp, testId: "nav-revenue" },
  { to: "/console/admin/tenants", label: "Tenants", icon: Building2, testId: "nav-tenants" },
  { to: "/console/admin/audit-logs", label: "Audit logs", icon: ScrollTextIcon, testId: "nav-audit-logs" },
];

// Resolve each item's roles at consumption time from ROUTE_ROLES so we
// don't repeat the mapping here.
const roleAllows = (path, role) => {
  const allow = ROUTE_ROLES[path];
  return !allow || allow.includes(role);
};

export default function ConsoleLayout() {
  const { user, tenant, logout, impersonating, stopImpersonation } = useAuth();
  const { theme, resolvedTheme, toggle } = useTheme();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clock, setClock] = useState("00:00:00.000");
  const [onAirCount, setOnAirCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    let ws;
    let retryTimer;
    let stopped = false;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl("/api/ws/channels"));
        ws.onmessage = (e) => {
          try {
            const d = JSON.parse(e.data);
            if (d.type === "tick") {
              setClock(d.master_clock);
              const onAir = d.channels.filter((c) => c.status === "on_air").length;
              setOnAirCount(onAir);
              setTotalCount(d.channels.length);
            }
          } catch (_) {
            // ignore parse errors
          }
        };
        ws.onclose = () => {
          if (!stopped) retryTimer = setTimeout(connect, 2500);
        };
        ws.onerror = () => {
          try { ws.close(); } catch (_) { /* noop */ }
        };
      } catch (_) {
        // ignore
      }
    };
    connect();
    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (ws) {
        try { ws.close(); } catch (_) { /* noop */ }
      }
    };
  }, []);

  // Show the "Admin" nav section for any role that has at least one entry
  // in ADMIN_NAV — tenant_admin, super_admin, and operator (Users only).
  const isAdmin = user && ADMIN_NAV.some((it) => roleAllows(it.to, user.role));

  // Live trial countdown — ticks every 60 s so the banner reflects
  // "1 day 4 hours left" precision without needing a full page refresh.
  const [trialTick, setTrialTick] = useState(0);
  useEffect(() => {
    if (!tenant?.is_trial) return;
    const id = setInterval(() => setTrialTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, [tenant]);

  const trialRemaining = useMemo(() => {
    if (!tenant?.is_trial || !tenant?.trial_ends_at) return null;
    const ms = new Date(tenant.trial_ends_at).getTime() - Date.now();
    if (ms <= 0) return { expired: true, ms: 0, days: 0, hours: 0, label: "expired" };
    const totalHours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    let label;
    if (days > 0) label = `${days}d ${hours}h left`;
    else if (hours > 0) label = `${hours}h left`;
    else label = "< 1h left";
    return { expired: false, ms, days, hours, label };
  }, [tenant, trialTick]);

  // Day-2 upgrade nudge — one-time toast, dismissed via localStorage so the
  // operator doesn't get pestered on every navigation.
  useEffect(() => {
    if (!trialRemaining || trialRemaining.expired) return;
    // Show ONLY when we're inside the last 48 hours but before the last 24
    // (that's what "day-2" means for a 3-day trial). Skip if already shown.
    if (trialRemaining.ms > 48 * 3600 * 1000) return;
    if (trialRemaining.ms < 24 * 3600 * 1000) return;
    const key = `moxyplay:trial-nudge:${tenant?.id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    // Late-import to keep the layout import graph clean
    import("sonner").then(({ toast }) => {
      toast.message("MOXYPLAY trial · day 2", {
        description: `${trialRemaining.label} on your sandbox. Upgrade any time to keep your channels, EPG feeds and graphics layers.`,
        duration: 12000,
        action: {
          label: "See plans",
          onClick: () => nav("/pricing"),
        },
      });
    });
  }, [trialRemaining, tenant?.id, nav]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Sidebar — static on md+, off-canvas drawer on mobile */}
      <aside
        data-testid="console-sidebar"
        className={`w-60 border-r border-border flex flex-col shrink-0 bg-background z-50 transition-transform duration-200
          fixed inset-y-0 left-0 md:static md:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2">
            {tenant?.branding?.logo_url ? (
              <div
                className="h-8 w-8 rounded-lg overflow-hidden shrink-0 bg-white flex items-center justify-center"
                data-testid="sidebar-logo"
              >
                <img
                  src={tenant.branding.logo_url}
                  alt={tenant.branding.product_name || "MOXYPLAY"}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div
                className="h-9 w-9 rounded-lg overflow-hidden shrink-0 p-1 flex items-center justify-center"
                data-testid="sidebar-logo"
                style={{ background: "var(--brand-bg-deep)", boxShadow: "0 0 12px rgba(139,92,246,0.35)" }}
              >
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_broadcast-cloud-2/artifacts/jeb6uhlp_FullLogo_Transparent_NoBuffer.png"
                  alt="MOXYPLAY"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div>
              <div className="font-display font-black text-sm leading-none text-gradient">
                {tenant?.branding?.product_name || "MOXYPLAY"}
              </div>
              <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{tenant?.name || "no tenant"}</div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close-btn"
              className="md:hidden ml-auto p-1.5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground shrink-0"
              title="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-2 overflow-y-auto scrollbar-thin" onClick={() => setSidebarOpen(false)}>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground px-2 pt-2 pb-1">Operations</div>
          {NAV.filter((item) => roleAllows(item.to, user?.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2 py-2 rounded-sm text-sm transition ${
                  isActive ? "bg-accent text-foreground border-l-2" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground border-l-2 border-transparent"
                }`
              }
              style={({ isActive }) => (isActive ? { borderLeftColor: "var(--tenant-primary)" } : {})}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground px-2 pt-4 pb-1">Admin</div>
              {ADMIN_NAV.filter((item) => roleAllows(item.to, user?.role)).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-2 py-2 rounded-sm text-sm transition ${
                      isActive ? "bg-accent text-foreground border-l-2" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground border-l-2 border-transparent"
                    }`
                  }
                  style={({ isActive }) => (isActive ? { borderLeftColor: "var(--tenant-primary)" } : {})}
                >
                  <item.icon className="w-4 h-4" strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-border flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold shrink-0"
            style={{ background: user?.avatar_color || "var(--tenant-primary)" }}
            data-testid="user-avatar"
          >
            {user?.name?.split(" ").slice(0, 2).map((s) => s[0]).join("") || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate text-foreground">{user?.name}</div>
            <div className="text-[10px] font-mono text-muted-foreground uppercase">{user?.role?.replace("_", " ")}</div>
          </div>
          <button
            onClick={toggle}
            data-testid="theme-toggle-btn"
            data-theme-state={theme}
            className="p-1.5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            title={
              theme === "system"
                ? `System theme (currently ${resolvedTheme}) · click for light`
                : theme === "light"
                  ? "Light theme · click for dark"
                  : "Dark theme · click to follow system"
            }
          >
            {theme === "system" ? (
              <Monitor className="w-4 h-4" />
            ) : theme === "light" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="p-1.5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Trial banner */}
        {trialRemaining && (
          <div
            data-testid="trial-banner"
            className={`px-4 py-2 border-b border-border flex items-center justify-between text-xs backdrop-blur-md ${
              trialRemaining.expired
                ? "bg-rose-500/15"
                : trialRemaining.ms < 24 * 3600 * 1000
                  ? "bg-rose-500/12"
                  : trialRemaining.ms < 48 * 3600 * 1000
                    ? "bg-amber-500/12"
                    : "bg-accent/50"
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  trialRemaining.expired
                    ? "bg-red-500 animate-pulse"
                    : trialRemaining.ms < 24 * 3600 * 1000
                      ? "bg-red-500 animate-pulse"
                      : trialRemaining.ms < 48 * 3600 * 1000
                        ? "bg-amber-400"
                        : "bg-emerald-400"
                }`}
              />
              <span className="font-mono text-[11px] uppercase tracking-widest text-foreground" data-testid="trial-remaining">
                {trialRemaining.expired ? "Trial expired" : `Trial mode · ${trialRemaining.label}`}
              </span>
              <span className="text-muted-foreground hidden md:inline">
                — sandbox expires on {new Date(tenant.trial_ends_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <button
              onClick={() => setUpgradeOpen(true)}
              data-testid="trial-upgrade-btn"
              className="text-[11px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm tenant-btn"
            >
              {trialRemaining.expired ? "Renew now" : "Upgrade"}
            </button>
          </div>
        )}
        <UpgradePlanDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

        {/* Cloudflare Stream quota banner — only renders at >=90% usage so
            operators can top up minutes before uploads start failing. */}
        <CloudflareQuotaBanner />

        {/* Topbar */}
        <div className="h-12 border-b border-border flex items-center px-4 gap-3 sm:gap-6 shrink-0 bg-card/40 backdrop-blur-md overflow-hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-open-btn"
            className="md:hidden p-1.5 -ml-1.5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground shrink-0"
            title="Open menu"
          >
            <Menu className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <span className="dot-live" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">
              On air {onAirCount}/{totalCount}
            </span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap truncate">
            Master clock: <span className="text-emerald-500 dark:text-emerald-400" data-testid="master-clock">{clock}</span>
          </div>
          <div className="ml-auto flex items-center gap-3 shrink-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap hidden sm:block">
              {new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto scrollbar-thin">
          {impersonating && (
            <div className="bg-fuchsia-500 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="impersonation-banner">
              <div className="flex items-center gap-2 font-display">
                <span className="text-[10px] font-mono uppercase tracking-widest bg-black/20 px-1.5 py-0.5 rounded-sm">
                  IMPERSONATING
                </span>
                <span>You&apos;re signed in as <b>{user?.email}</b> at <b>{tenant?.name}</b>. 1-hour session · every action is logged to the tenant&apos;s audit trail.</span>
              </div>
              <button
                onClick={stopImpersonation}
                className="text-[11px] font-mono uppercase tracking-widest bg-white/15 hover:bg-white/25 px-3 py-1 rounded-sm"
                data-testid="stop-impersonation"
              >
                Return to super admin
              </button>
            </div>
          )}
          <Outlet />
        </div>
      </main>
      {trialRemaining?.expired && (
        <TrialPaywall tenant={tenant} onUpgraded={() => window.location.reload()} />
      )}
    </div>
  );
}
